import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getItemById, checkoutItemWithDue, checkinItem, getItemCheckoutHistory, getStudentByPid } from '../utils/itemOperations';
import { getPublishedGuide } from '../utils/guideOperations';
import { 
  checkoutReservedItem, 
  checkinReservedItem, 
  getActiveReservation, 
  getUpcomingReservation,
  getUserReservations,
  cancelReservation,
  returnReservationEarly
} from '../utils/reservationOperations';
import Layout from '../components/layout/Layout';
import GuideEditor from '../components/guides/GuideEditor';
import GuideViewer from '../components/guides/GuideViewer';
import SimpleWysiwygEditor from '../components/guides/SimpleWysiwygEditor';
import ReservationForm from '../components/items/ReservationForm';
import ItemReservationsCalendar from '../components/items/ItemReservationsCalendar';
import SimpleDateTimePicker from '../components/ui/SimpleDateTimePicker';
import ConsumableUsageForm from '../components/consumables/ConsumableUsageForm';
import ConsumableUsageHistory from '../components/consumables/ConsumableUsageHistory';
import { useAuth } from '../context/AuthContext';

const ItemDetail = () => {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [students, setStudents] = useState([]);
  const [pidInput, setPidInput] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pidError, setPidError] = useState('');
  const [checkoutDue, setCheckoutDue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [guide, setGuide] = useState(null);
  const [reservationsReloadKey, setReservationsReloadKey] = useState(0);
  const [usageReloadKey, setUsageReloadKey] = useState(0);
  const [showGuideEditor, setShowGuideEditor] = useState(false);
  const [showGuideViewer, setShowGuideViewer] = useState(false);
  const [showWysiwygEditor, setShowWysiwygEditor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeReservation, setActiveReservation] = useState(null);
  const [upcomingReservation, setUpcomingReservation] = useState(null);
  const [userReservations, setUserReservations] = useState([]);
  const navigate = useNavigate();
  const { currentUser, userDetails } = useAuth();

  // Check if user is admin
  useEffect(() => {
    const adminStatus = userDetails?.role === 'admin';
    setIsAdmin(adminStatus);
  }, [userDetails?.role]);

  // Define loadCheckoutHistory function with useCallback to prevent recreation on every render
  const loadCheckoutHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const historyData = await getItemCheckoutHistory(itemId);
      setHistory(historyData);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [itemId]);

  // Load guide data
  const loadGuide = useCallback(async () => {
    try {
      console.log('Loading guide for itemId:', itemId);
      const publishedGuide = await getPublishedGuide(itemId);
      console.log('Published guide result:', publishedGuide);
      setGuide(publishedGuide);
    } catch (error) {
      console.error('Error loading guide:', error);
    }
  }, [itemId]);

  // Load reservation data
  const loadReservations = useCallback(async () => {
    try {
      const [active, upcoming] = await Promise.all([
        getActiveReservation(itemId),
        getUpcomingReservation(itemId)
      ]);
      setActiveReservation(active);
      setUpcomingReservation(upcoming);
    } catch (error) {
      console.error('Error loading reservations:', error);
    }
  }, [itemId]);

  // Load user's own reservations
  const loadUserReservations = useCallback(async () => {
    if (!currentUser || !userDetails) return;
    try {
      let reservations = [];
      if (userDetails?.pid) {
        // PID users: query by PID for backward compatibility with older reservations
        reservations = await getUserReservations(itemId, { pid: userDetails.pid });
      } else {
        // Email/password users: query by userId
        reservations = await getUserReservations(itemId, { userId: currentUser.uid });
      }
      setUserReservations(reservations);
    } catch (error) {
      console.error('Error loading user reservations:', error);
    }
  }, [itemId, currentUser, userDetails]);

  const handleReservationCreated = () => {
    // bump key to notify calendar to refetch via key change on component
    setReservationsReloadKey((k) => k + 1);
    loadReservations(); // Reload reservation data
    loadUserReservations(); // Reload user's reservations
  };

  // Handle PID lookup
  const handlePidLookup = async () => {
    if (!pidInput.trim()) {
      setPidError('Please enter a PID');
      return;
    }

    try {
      const student = await getStudentByPid(pidInput);
      if (student) {
        setSelectedStudent(student);
        setPidError('');
        setError('');
      } else {
        setPidError('No student found with this PID');
        setSelectedStudent(null);
      }
    } catch (error) {
      console.error('Error looking up student:', error);
      setPidError('Error looking up student. Please try again.');
      setSelectedStudent(null);
    }
  };

  useEffect(() => {
    const fetchItem = async () => {
      try {
        setLoading(true);
        const fetchedItem = await getItemById(itemId);
        setItem(fetchedItem);
        loadCheckoutHistory();
        loadGuide();
        loadReservations();
        loadUserReservations();
      } catch (error) {
        console.error('Error fetching item:', error);
        setError('Failed to load item details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [itemId, loadCheckoutHistory, loadGuide, loadReservations, loadUserReservations]);

  const handleCheckout = async () => {
    if (!selectedStudent) {
      setError('Please enter and verify student PID');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      if (!checkoutDue) {
        setError('Please select a due date/time');
        return;
      }
      const dueDate = new Date(checkoutDue);
      if (Number.isNaN(dueDate.getTime())) {
        setError('Invalid due date/time');
        return;
      }
      if (dueDate.getTime() <= Date.now()) {
        setError('Due time must be in the future');
        return;
      }
      await checkoutItemWithDue(itemId, selectedStudent.id, selectedStudent.name, dueDate);
      
      // Refresh item data
      const updatedItem = await getItemById(itemId);
      setItem(updatedItem);
      
      // Format the due date for display in success message
      const formattedDueDate = new Date(checkoutDue).toLocaleString();
      
      setSuccess(`Item checked out successfully to ${selectedStudent.name}! Due back by ${formattedDueDate}.`);
      loadCheckoutHistory();
    } catch (error) {
      console.error('Error checking out item:', error);
      setError('Failed to check out item: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleCheckin = async () => {
    if (!item || !item.currentHolderId) {
      setError('This item is not currently checked out');
      return;
    }
    
    if (!selectedStudent) {
      setError('Please enter and verify student PID');
      return;
    }
    
    // Verify the student PID matches the current holder
    if (selectedStudent.id !== item.currentHolderId) {
      setError('PID does not match the student who checked out this item');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await checkinItem(itemId, item.currentHolderId);
      
      // Refresh item data
      const updatedItem = await getItemById(itemId);
      setItem(updatedItem);
      
      setSuccess('Item returned successfully!');
      loadCheckoutHistory();
      setSelectedStudent(null); // Reset student selection after check-in
      setPidInput(''); // Clear PID input
    } catch (error) {
      console.error('Error checking in item:', error);
      setError('Failed to return item: ' + (error.message || 'Unknown error'));
      return; // Stop execution on error
    } finally {
      setLoading(false);
    }
  };

  const handleReservationCheckout = async (reservationId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await checkoutReservedItem(itemId, reservationId);
      
      // Refresh item and reservation data
      const updatedItem = await getItemById(itemId);
      setItem(updatedItem);
      loadReservations();
      loadCheckoutHistory();
      
      setSuccess('Item checked out successfully via reservation!');
    } catch (error) {
      console.error('Error checking out reserved item:', error);
      setError('Failed to check out reserved item: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReservationCheckin = async (reservationId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await checkinReservedItem(itemId, reservationId);
      
      // Refresh item and reservation data
      const updatedItem = await getItemById(itemId);
      setItem(updatedItem);
      loadReservations();
      loadCheckoutHistory();
      
      setSuccess('Item returned successfully via reservation!');
    } catch (error) {
      console.error('Error checking in reserved item:', error);
      setError('Failed to return reserved item: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReturnReservationEarly = async (reservationId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await returnReservationEarly(itemId, reservationId);
      
      // Refresh all data
      const updatedItem = await getItemById(itemId);
      setItem(updatedItem);
      loadReservations();
      loadUserReservations();
      setReservationsReloadKey((k) => k + 1); // Refresh calendar
      
      setSuccess('Reservation returned successfully!');
    } catch (error) {
      console.error('Error returning reservation:', error);
      setError('Failed to return reservation: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUsageRecorded = async () => {
    try {
      // Refresh item data to get updated stock levels
      const updatedItem = await getItemById(itemId);
      setItem(updatedItem);
      setUsageReloadKey((k) => k + 1); // Refresh usage history
    } catch (error) {
      console.error('Error refreshing item data:', error);
    }
  };

  const handleGuideSave = () => {
    setShowGuideEditor(false);
    setShowWysiwygEditor(false);
    loadGuide(); // Reload guide data
  };

  if (loading && !item) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading item details...</div>
        </div>
      </Layout>
    );
  }

  if (error && !item) {
    return (
      <Layout>
        <div className="p-4 border border-red-300 bg-red-50 rounded-md">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => navigate('/items')}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Items
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Item Header */}
          <div className="bg-black px-6 py-4">
            <div className="flex justify-between items-center mb-2">
              <div>
                <h1 className="text-2xl font-bold text-white">{item?.name}</h1>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Guide Buttons */}
                <button
                  onClick={() => setShowGuideViewer(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded shadow"
                >
                  View Guide
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setShowWysiwygEditor(true)}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded shadow"
                  >
                    Edit Guide
                  </button>
                )}
                <button 
                  onClick={() => navigate('/scan')} 
                  className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded shadow">
                  Back to Scanner
                </button>
                <button 
                  onClick={() => navigate('/')} 
                  className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded shadow">
                  Dashboard
                </button>
              </div>
            </div>
          </div>
          
          {/* Success/Error Messages */}
          {success && (
            <div className="m-6 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md">
              {success}
            </div>
          )}
          
          {error && (
            <div className="m-6 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Item Image */}
              <div className="md:w-1/3">
                {item?.imageUrl ? (
                  <img 
                    src={item.imageUrl} 
                    alt={item.name} 
                    className="w-full h-auto object-cover rounded-md"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center rounded-md">
                    <p className="text-gray-500">No image available</p>
                  </div>
                )}
              </div>
              
              {/* Item Details */}
              <div className="md:w-2/3">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold mb-2">Details</h2>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="font-medium">Category:</span>
                      <span>{item?.category || 'Uncategorized'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="font-medium">Status:</span>
                      <span className={`py-1 px-2 rounded-full text-sm font-medium ${
                        item?.status === 'Available' 
                          ? 'bg-green-100 text-green-800' 
                          : item?.status === 'Checked Out' 
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item?.status}
                      </span>
                    </div>
                    {item?.status === 'Checked Out' && (
                      <>
                        <div className="flex justify-between py-2 border-b">
                          <span className="font-medium">Checked out by:</span>
                          <span>{currentUser ? (item?.currentHolderName || 'Unknown') : 'Lab Member'}</span>
                        </div>
                        {item?.dueDate && (
                          <div className="flex justify-between py-2 border-b">
                            <span className="font-medium">Due date:</span>
                            <span className="font-bold text-red-600">
                              {new Date(item.dueDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {item?.lastCheckoutTime && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="font-medium">Last checkout:</span>
                        <span>{new Date(item.lastCheckoutTime).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mb-4">
                  <h2 className="text-xl font-semibold mb-2">Description</h2>
                  <p className="text-gray-700">{item?.description || 'No description available.'}</p>
                </div>

                {/* Guide Status - Only show if guide exists or user is admin */}
                {(guide || isAdmin) && (
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold mb-2">Equipment Guide</h2>
                    {guide ? (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-green-800 font-medium">Guide Available</p>
                            <p className="text-green-600 text-sm">
                              Version {guide.version} - Last updated {new Date(guide.updatedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => setShowGuideViewer(true)}
                            className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                          >
                            View Guide
                          </button>
                        </div>
                      </div>
                    ) : isAdmin && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                        <div className="flex items-center justify-between">
                          <p className="text-gray-600 text-sm">No guide available for this item</p>
                          <button
                            onClick={() => setShowWysiwygEditor(true)}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                          >
                            Create Guide
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Reservation Form - Only for Equipment */}
                {item?.itemType !== 'consumable' && (
                  <ReservationForm 
                    itemId={itemId}
                    itemName={item?.name}
                    onCreated={handleReservationCreated}
                  />
                )}

                {/* Consumables Usage Form */}
                {item?.itemType === 'consumable' && (
                  <ConsumableUsageForm 
                    item={item}
                    onUsageRecorded={handleUsageRecorded}
                  />
                )}

                {/* User's Own Reservations - Only for Equipment */}
                {item?.itemType !== 'consumable' && userReservations.length > 0 && (
                  <div className="mb-8 p-4 bg-purple-50 rounded-lg">
                    <h3 className="text-lg font-medium mb-3">📋 Your Reservations</h3>
                    <div className="space-y-3">
                      {userReservations.map((reservation) => {
                        const now = new Date();
                        const endTime = reservation.end instanceof Object && reservation.end.toDate ? reservation.end.toDate() : new Date(reservation.end);
                        const isExpired = (reservation.status === 'reserved' || reservation.status === 'checked_out') && endTime < now;
                        const displayStatus = isExpired ? 'expired' : reservation.status;
                        
                        return (
                          <div key={reservation.id} className="p-3 bg-white rounded-md border border-purple-200">
                            <div className="flex justify-between items-start">
                              <div className="text-sm">
                                <p><strong>From:</strong> {new Date(reservation.start.toDate()).toLocaleString()}</p>
                                <p><strong>Until:</strong> {new Date(reservation.end.toDate()).toLocaleString()}</p>
                                {reservation.notes && <p><strong>Notes:</strong> {reservation.notes}</p>}
                                <p><strong>Status:</strong> 
                                  <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                                    displayStatus === 'reserved' 
                                      ? 'bg-blue-100 text-blue-800' 
                                      : displayStatus === 'checked_out'
                                        ? 'bg-orange-100 text-orange-800'
                                        : displayStatus === 'expired'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {displayStatus === 'reserved' ? 'Reserved' : 
                                     displayStatus === 'checked_out' ? 'Checked Out' : 
                                     displayStatus === 'expired' ? 'Expired' :
                                     displayStatus}
                                  </span>
                                </p>
                              </div>
                              {(reservation.status === 'reserved' || reservation.status === 'checked_out') && !isExpired && (
                                <button
                                  onClick={() => handleReturnReservationEarly(reservation.id)}
                                  disabled={loading}
                                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {loading ? '⏳' : '🔙 Return Early'}
                                </button>
                              )}
                              {isExpired && (
                                <span className="px-3 py-1 bg-gray-200 text-gray-600 text-sm rounded">
                                  Expired
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Active Reservation - Check In */}
                {activeReservation && item?.status === 'Checked Out' && item?.reservationId === activeReservation.id && (
                  <div className="mb-8 p-4 bg-orange-50 rounded-lg">
                    <h3 className="text-lg font-medium mb-3">Active Reservation</h3>
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600">
                        <p><strong>Reserved by:</strong> {activeReservation.userName || `PID: ${activeReservation.pid}`}</p>
                        <p><strong>Until:</strong> {new Date(activeReservation.end.toDate()).toLocaleString()}</p>
                        {activeReservation.notes && <p><strong>Notes:</strong> {activeReservation.notes}</p>}
                      </div>
                      <button
                        onClick={() => handleReservationCheckin(activeReservation.id)}
                        disabled={loading}
                        className="w-full py-3 px-4 bg-orange-500 text-white font-medium rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Return Reserved Item'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Upcoming Reservation - Check Out */}
                {upcomingReservation && item?.status === 'Available' && (
                  <div className="mb-8 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-lg font-medium mb-3">Upcoming Reservation</h3>
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600">
                        <p><strong>Reserved by:</strong> {upcomingReservation.userName || `PID: ${upcomingReservation.pid}`}</p>
                        <p><strong>From:</strong> {new Date(upcomingReservation.start.toDate()).toLocaleString()}</p>
                        <p><strong>Until:</strong> {new Date(upcomingReservation.end.toDate()).toLocaleString()}</p>
                        {upcomingReservation.notes && <p><strong>Notes:</strong> {upcomingReservation.notes}</p>}
                      </div>
                      <button
                        onClick={() => handleReservationCheckout(upcomingReservation.id)}
                        disabled={loading}
                        className="w-full py-3 px-4 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Check Out Reserved Item'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Consumables Usage History */}
            {item?.itemType === 'consumable' && (
              <div className="mt-8">
                <ConsumableUsageHistory 
                  itemId={itemId}
                  reloadKey={usageReloadKey}
                />
              </div>
            )}
            
            {/* Checkout History - Only for Equipment */}
            {item?.itemType !== 'consumable' && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4">Checkout History</h2>
              {historyLoading ? (
                <p className="text-gray-600">Loading history...</p>
              ) : history.length === 0 ? (
                <p className="text-gray-600">No checkout history available.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Checked Out
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Returned
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {history.map((record) => (
                        <tr key={record.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{currentUser ? (record.studentName || 'Unknown') : 'Lab Member'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(record.timestamp).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {record.returnTimestamp ? new Date(record.returnTimestamp).toLocaleString() : 'Not returned'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              record.action === 'checkin' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {record.action === 'checkin' ? 'Returned' : 'Checked Out'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            )}

            {/* Per-item Reservations Calendar - Only for Equipment */}
            {item?.itemType !== 'consumable' && (
              <ItemReservationsCalendar key={`cal-${reservationsReloadKey}`} itemId={itemId} itemName={item?.name} />
            )}
          </div>
        </div>
      </div>

      {/* WYSIWYG Guide Editor Modal */}
      {showWysiwygEditor && (
        <SimpleWysiwygEditor
          itemId={itemId}
          itemName={item?.name}
          initialContent={guide?.content || ''}
          initialTitle={guide?.title || ''}
          onClose={() => setShowWysiwygEditor(false)}
          onSave={handleGuideSave}
        />
      )}

      {/* Guide Editor Modal (Legacy) */}
      {showGuideEditor && (
        <GuideEditor
          itemId={itemId}
          itemName={item?.name}
          onClose={() => setShowGuideEditor(false)}
          onSave={handleGuideSave}
          isAdmin={isAdmin}
        />
      )}

      {/* Guide Viewer Modal */}
      {showGuideViewer && (
        <GuideViewer
          guide={guide}
          item={item}
          onClose={() => setShowGuideViewer(false)}
        />
      )}
    </Layout>
  );
};

export default ItemDetail;
